import api from "@/api";
import deepClone from "@/utils/deep-clone";

const state = {
    details: {},
    preview: {},
};

const getters = {
    details(state) {
        return id => deepClone(state.details[id] || {});
    },
    preview(state) {
        return state.preview;
    },
};

const actions = {
    get({ commit, rootGetters }, { payload: { id } }) {
        const user = rootGetters["users/current/user"];

        return api
            .get(`/api/reviewcycles/get/details/${id}`)
            .then(res => {
                const data = res.data;
                const reviewCycle = data;

                if (!data.questions) {
                    reviewCycle.questions = { skills: [], settings: {} };
                }

                if (!data.participationRules) {
                    reviewCycle.participationRules = {
                        include: {
                            all: true,
                            users: [],
                            localTeams: [],
                            locations: [],
                        },
                        exclude: {
                            users: [],
                            localTeams: [],
                            locations: [],
                            tenureInWeeksLessThan: 0,
                        },
                    };
                } else {
                    // match FE data structure

                    reviewCycle.participationRules.include.locations = data.participationRules.include.locations.map(
                        function(l) {
                            return { name: l };
                        },
                    );
                    reviewCycle.participationRules.exclude.locations = data.participationRules.exclude.locations.map(
                        function(l) {
                            return { name: l };
                        },
                    );
                    reviewCycle.participationRules.include.users = data.participationRules.include.users.map(
                        function(u) {
                            return { name: u.fullname, _id: u._id };
                        },
                    );
                    reviewCycle.participationRules.exclude.users = data.participationRules.exclude.users.map(
                        function(u) {
                            return { name: u.fullname, _id: u._id };
                        },
                    );
                }

                // lock settings

                if (
                    reviewCycle.isCreatedFromTemplate &&
                    reviewCycle.createdAsRole != "admin" &&
                    !reviewCycle.automation &&
                    !user.rights.R_CREATE_TEMPLATE
                ) {
                    // manager or employees created a cycle from a template
                    reviewCycle.questionsLocked = true;
                    reviewCycle.questionSettingsLocked = true;
                    reviewCycle.scopeLocked = true;
                    reviewCycle.visibilityLocked = true;
                } else if (reviewCycle.status.globalStatus != "draft") {
                    // active cycle
                    reviewCycle.questionsLocked = true;
                    reviewCycle.questionSettingsLocked = false;
                    reviewCycle.scopeLocked = true;
                    reviewCycle.visibilityLocked = false;
                } else {
                    // all others
                    reviewCycle.questionsLocked = true;
                    reviewCycle.questionSettingsLocked = false;
                    reviewCycle.scopeLocked = false;
                    reviewCycle.visibilityLocked = false;
                }

                if (!reviewCycle.customNotifications) {
                    reviewCycle.customNotifications = {
                        kickoff: null,
                        daily: null,
                        weekly: null,
                    };
                }

                commit("updateDetails", { reviewCycle });
            })
            .catch(err => {
                if (err?.response?.status === 404) {
                    return;
                }

                throw err;
            });
    },

    update({ commit, state }, { payload: { reviewCycle, scope } }) {
        const prevReviewCycle = getters.details(state)(reviewCycle._id);

        // Basics
        if (scope === "basics") {
            return api
                .post("/api/reviewcycles/create", {
                    reviewCycle: reviewCycle._id,
                    owners: reviewCycle.owners,
                    name: reviewCycle.name,
                    description: reviewCycle.description,
                    templateForManagers: reviewCycle.templateForManagers,
                    templateForEmployees: reviewCycle.templateForEmployees,
                })
                .then(res => {
                    const data = res.data;

                    commit("updateDetails", {
                        reviewCycle: {
                            ...prevReviewCycle,
                            scale: data.reviewCycle.scale,
                        },
                    });
                });
        }

        // Participants
        if (scope === "participants") {
            const includeRules = reviewCycle.participationRules.include;
            const excludeRules = reviewCycle.participationRules.exclude;

            const participationRules = {
                include: {
                    all: Boolean(includeRules.all),
                    users: includeRules.users.map(u => u._id),
                    localTeams: includeRules.localTeams.map(t => t._id),
                    locations: includeRules.locations.map(l => l.name),
                    customAttributes: includeRules.customAttributes,
                },
                exclude: {
                    users: excludeRules.users.map(u => u._id),
                    localTeams: excludeRules.localTeams.map(t => t._id),
                    locations: excludeRules.locations.map(l => l.name),
                    tenureInWeeksLessThan: excludeRules.tenureInWeeksLessThan,
                    customAttributes: excludeRules.customAttributes,
                },
            };

            return api.post("/api/reviewcycles/update/participationrules", {
                reviewCycleId: reviewCycle._id,
                participationRules,
            });
        }

        // Scope
        if (scope === "scope") {
            return api.post("/api/reviewcycles/update/scope", reviewCycle).then(() => {
                commit("updateDetails", { reviewCycle });
            });
        }

        // Questions
        if (scope === "questions") {
            const questions = [];

            reviewCycle.questions?.selected?.forEach?.(function(question) {
                if (question.name.length)
                    questions.push({
                        _id: question._id,
                        name: question.name,
                        description: question.description,
                        type: question.type,
                        roleReviewee: question.roleReviewee,
                        roleManager: question.roleManager,
                        rolePeer: question.rolePeer,
                        roleDirectReport: question.roleDirectReport,
                        private: question.private,
                        weight: question.weight,
                        scale: question.scale,
                        commentRequirement:
                            question.commentRequirement === "hidden" &&
                            question.ratingRequirement === "hidden" &&
                            question.answerType !== "multipleChoice"
                                ? "optional"
                                : question.commentRequirement,
                        ratingRequirement:
                            question.commentRequirement === "hidden" &&
                            question.ratingRequirement === "hidden" &&
                            question.answerType !== "multipleChoice"
                                ? "optional"
                                : question.ratingRequirement,
                        answerType: question.answerType,
                        multipleChoiceOptions: question.multipleChoiceOptions,
                        allowMultipleAnswers: question.allowMultipleAnswers,
                        translations: question.translations,
                    });
            });

            return api
                .post("/api/reviewcycles/update/questionsettings", {
                    reviewCycle: reviewCycle._id,
                    questions: questions,
                    companySkills: reviewCycle.questions?.companySkills,
                    teamSkills: reviewCycle.questions?.teamSkills,
                    goals: reviewCycle.questions?.goals,
                    customScoresEnabled: reviewCycle.questions?.customScoresEnabled,
                    scale: reviewCycle.scale,
                })
                .then(function(response) {
                    reviewCycle.questions.selected.forEach(function(question, idx) {
                        if (!question._id) {
                            var q = response.data.questions.skills.find(function(q) {
                                return q.name == question.name && !q._id;
                            });
                            reviewCycle.questions.selected[idx]._id = q._id;
                        }
                    });

                    commit("updateDetails", { reviewCycle });
                });
        }

        // Goals
        if (scope === "goals") {
            return api.post("/api/reviewcycles/update/goals", { reviewCycleId: reviewCycle._id });
        }

        // Skill Assignment
        if (scope === "skillAssignment") {
            return api.post("/api/reviewcycles/update/skillAssignment", {
                reviewCycleId: reviewCycle._id,
            });
        }

        // Visibility
        if (scope === "visibility") {
            return api.post("/api/reviewcycles/update/visibility", reviewCycle).then(() => {
                commit("updateDetails", { reviewCycle });
            });
        }

        // Timeline
        if (scope === "timeline") {
            const data = {
                reviewCycle: reviewCycle._id,
                isTemplate: reviewCycle.isTemplate,
                selfEvaluationsEnabled: reviewCycle.selfEvaluationsEnabled,
                peerReviewsEnabled: reviewCycle.peerReviewsEnabled,
                peerNominationsEnabled: reviewCycle.peerNominationsEnabled,
                peerApprovalEnabled: reviewCycle.peerApprovalEnabled,
                peerAutoApprovalEnabled: reviewCycle.peerAutoApprovalEnabled,
                managerReviewsEnabled: reviewCycle.managerReviewsEnabled,
                conversationsEnabled: reviewCycle.conversationsEnabled,
                startPlan: reviewCycle.status.startPlan,
                peerNominationsPlan: reviewCycle.status.peerNominationsPlan,
                peerApprovalPlan: reviewCycle.status.peerApprovalPlan,
                peerReviewsPlan: reviewCycle.status.peerReviewsPlan,
                managerReviewsPlan: reviewCycle.status.managerReviewsPlan,
                conversationsPlan: reviewCycle.status.conversationsPlan,
                globalStatus: reviewCycle.status.globalStatus,
                peerNominationsDelay: reviewCycle.status.peerNominationsDelay,
                peerApprovalDelay: reviewCycle.status.peerApprovalDelay,
                peerReviewsDelay: reviewCycle.status.peerReviewsDelay,
                managerReviewsDelay: reviewCycle.status.managerReviewsDelay,
                conversationsDelay: reviewCycle.status.conversationsDelay,
            };

            // call API

            return api.post("/api/reviewcycles/update/timeline", data).then(() => {
                commit("updateDetails", { reviewCycle });
            });
        }

        // Notifications
        if (scope === "notifications") {
            return api.post("/api/reviewcycles/update/notifications", reviewCycle).then(res => {
                const data = res.data;

                commit("updateDetails", {
                    reviewCycle: {
                        ...prevReviewCycle,
                        status: {
                            ...prevReviewCycle.status,
                            nextReminderOn: data.status.nextReminderOn,
                        },
                    },
                });
            });
        }
    },

    updateStatus({ commit, state }, { payload: { id, status } }) {
        const prevReviewCycle = getters.details(state)(id);

        return api
            .post("/api/reviewcycles/update/status", {
                reviewCycle: id,
                newStatus: status,
            })
            .then(res => {
                const data = res.data;

                commit("updateDetails", {
                    reviewCycle: {
                        ...prevReviewCycle,
                        status: data.status,
                    },
                });
            });
    },

    deactivate(_, { payload: { id } }) {
        return api.post("/api/reviewcycles/update/status", {
            reviewCycle: id,
            newStatus: "canceled",
        });
    },
};

const mutations = {
    updateDetails(state, { reviewCycle }) {
        if (!reviewCycle._id) {
            return;
        }

        state.details[reviewCycle._id] = reviewCycle;
    },

    updatePreview(state, { preview }) {
        state.preview = preview;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
